import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, MoreHorizontal, SlidersHorizontal, Trash2, Columns } from '../../../icons';
import { useAnchoredMenu } from '../../../hooks/useAnchoredMenu';
import { useDashboardStore, type BoardWidget, type DashboardBoard, type WidgetSpan } from '../../../store/dashboardStore';
import { getWidgetDef } from './widget-registry';
import { WidgetChromeContext, DEFAULT_CHROME } from './widget-chrome';
import { WidgetSettingsModal } from './WidgetSettingsModal';
// 대시보드 시안 v2 V2 (2026-09-15) — 위젯 설정 v2 창. 깃발이 꺼져 있으면(기본) 종전 창 그대로.
import { WidgetSettingsV2Modal } from './WidgetSettingsV2Modal';
import { DASHBOARD_V2_ENABLED } from '../../../lib/layout-shell';

/**
 * 12-컬럼 위젯 그리드 — 드래그 재배치 + 폭 변경 + 제거.
 *
 * 드래그는 브라우저 기본 HTML5 DnD 만 쓴다(외부 DnD 라이브러리 추가 없음 = 번들 증가 0).
 * 카드 전체를 draggable 로 두면 카드 안의 버튼·링크를 누를 때도 드래그가 걸리므로,
 * **핸들(⠿)에서 시작한 드래그만** 허용한다: mousedown 이 `[data-dw-grip]` 안에서
 * 났는지 슬롯이 판정하고, 아니면 dragstart 를 취소한다.
 *
 * 잠긴 보드(#2b 전사)는 draggable 자체를 끈다 — 핸들도 그리지 않는다.
 */

interface WidgetGridProps {
  board: DashboardBoard;
}

/** 대시보드 시안 v2 V5 (2026-09-16) — 놓을 자리 글자. 옮기는 위젯의 폭을 적는다(「여기에 놓기 · 폭 1/3」). */
function dropLabel(board: DashboardBoard, from: number | null): string {
  const span = from === null ? undefined : board.widgets[from]?.span;
  if (!span) return '여기에 놓기';
  return `여기에 놓기 · 폭 ${span === 4 ? '1/3' : span === 6 ? '1/2' : span === 8 ? '2/3' : '전체'}`;
}

export function WidgetGrid({ board }: WidgetGridProps) {
  const moveWidget = useDashboardStore((s) => s.moveWidget);
  const editable = !board.locked;

  const dragFrom = useRef<number | null>(null);
  const gripArmed = useRef(false);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const onDrop = (toIndex: number) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    gripArmed.current = false;
    setOverIndex(null);
    if (from === null || from === toIndex) return;
    moveWidget(board.id, from, toIndex);
  };

  return (
    <div className="dw-grid">
      {board.widgets.map((placed, index) => {
        const def = getWidgetDef(placed.widgetKey);
        if (!def) return null;   // 저장된 배치에 남은 옛 위젯 key — 조용히 건너뛴다.
        const { Component } = def;
        return (
          <div
            key={placed.id}
            className={`dw-slot dw-slot--${placed.span}${overIndex === index ? ' is-over' : ''}`}
            data-drop={DASHBOARD_V2_ENABLED && overIndex === index ? dropLabel(board, dragFrom.current) : undefined}
            draggable={editable}
            onMouseDown={(e) => {
              gripArmed.current = (e.target as HTMLElement).closest('[data-dw-grip]') != null;
            }}
            onDragStart={(e) => {
              if (!editable || !gripArmed.current) { e.preventDefault(); return; }
              dragFrom.current = index;
              e.dataTransfer.effectAllowed = 'move';
              // Firefox 는 데이터가 없으면 드래그를 시작하지 않는다.
              e.dataTransfer.setData('text/plain', placed.id);
            }}
            onDragOver={(e) => {
              if (dragFrom.current === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (overIndex !== index) setOverIndex(index);
            }}
            onDragLeave={() => setOverIndex((cur) => (cur === index ? null : cur))}
            onDrop={(e) => { e.preventDefault(); onDrop(index); }}
            onDragEnd={() => { dragFrom.current = null; gripArmed.current = false; setOverIndex(null); }}
          >
            <WidgetFrame board={board} placed={placed} editable={editable}>
              <Component />
            </WidgetFrame>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 위젯 1개의 껍데기 — 도구(손잡이 · 설정 · ⋯)를 카드 위에 겹쳐 얹는다.
 *
 * ★★★ **도구는 편집 중에만 보인다**(2026-09-08 · 시안). 보기 상태의 화면에서 아이콘 셋이
 *     열넷 곱하기로 떠 있으면 그 화면은 «조작판»이 된다. 감추는 것은 CSS 가 하고
 *     (`.dw-board.is-editing`), 여기서는 늘 그린다 — 편집을 켤 때마다 붙였다 떼면
 *     그 순간 카드가 한 번 다시 그려진다.
 *
 * ★ 카드(`WidgetCard`)는 위젯이 직접 그린다. 도구를 카드 props 로 내려보내려면 위젯 14종이
 *   전부 도구를 받아 넘겨야 하므로, 여기서 absolute 로 겹쳐 얹어 위젯 코드를 오염시키지 않는다.
 */
function WidgetFrame({
  board, placed, editable, children,
}: {
  board: DashboardBoard;
  placed: BoardWidget;
  editable: boolean;
  children: React.ReactNode;
}) {
  const setWidgetSpan = useDashboardStore((s) => s.setWidgetSpan);
  const removeWidget = useDashboardStore((s) => s.removeWidget);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * ⋯ 메뉴 — **body 로 띄운다** (2026-09-09 · 사용자 지적 「삭제를 눌러도 안 된다」).
   *
   * ⚠⚠⚠ 종전에는 메뉴가 카드 «안»에 `position:absolute; z-index:5` 로 있었고, 바깥을 닫는
   *   가림막(`.dw-menu__scrim`)은 `position:fixed; z-index:100` 이었다 — **가림막이 메뉴 위에
   *   깔려** 항목을 눌러도 그 클릭이 가림막에 먹혔다. 메뉴는 그냥 닫히고 아무 일도 안 일어난다.
   *   폭 바꾸기·위젯 설정·위젯 제거가 **셋 다** 그랬다.
   * ★ 덤으로 `.dw-board` 가 `overflow:auto` 라 아래쪽 위젯의 메뉴는 잘리기도 했다 —
   *   탭 ⋯ 메뉴와 **같은 함정**이라 같은 고침(포털 + `useAnchoredMenu`)을 쓴다.
   */
  const menu = useAnchoredMenu<HTMLButtonElement>({ width: 186, height: 232 });
  const open = menu.pos !== null;
  const frameRef = useRef<HTMLDivElement | null>(null);

  const chrome = {
    ...DEFAULT_CHROME,
    ...(placed.settings?.title ? { title: placed.settings.title } : {}),
    ...(placed.settings?.showTitle !== undefined ? { showTitle: placed.settings.showTitle } : {}),
    ...(placed.settings?.height ? { height: placed.settings.height } : {}),
    settings: placed.settings ?? {},
    // 못 고치는 보드에서는 넘기지 않는다 — 위젯이 「추가」 타일을 그리지 않게.
    ...(editable ? { openSettings: () => setSettingsOpen(true) } : {}),
  };

  /**
   * 폭 손잡이 — 카드 오른쪽 모서리를 끌어 늘린다 (시안 화면 B).
   *
   * ★ 한 칸의 폭은 **격자에서 잰다**(카드 폭 ÷ 지금 칸 수). 상수로 두면 창을 좁히는 순간
   *   손잡이가 화면과 다른 값을 집는다.
   * ★ 포인터 캡처를 건다 — 없으면 카드 밖으로 나가는 순간 끌기가 끊긴다.
   */
  const onResizeDown = (e: React.PointerEvent) => {
    const el = frameRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startSpan = placed.span;
    const colW = el.getBoundingClientRect().width / startSpan;

    const move = (ev: PointerEvent) => {
      const delta = Math.round((ev.clientX - startX) / colW);
      const next = Math.min(12, Math.max(4, startSpan + delta)) as WidgetSpan;
      // 격자가 넷뿐이라 가장 가까운 칸으로 붙인다(5칸짜리 카드는 없다).
      const snapped = ([4, 6, 8, 12] as WidgetSpan[])
        .reduce((best, s) => (Math.abs(s - next) < Math.abs(best - next) ? s : best), 4 as WidgetSpan);
      if (snapped !== placed.span) setWidgetSpan(board.id, placed.id, snapped);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <WidgetChromeContext.Provider value={chrome}>
      <div className="dw-frame" ref={frameRef} data-locked={board.locked || undefined}>
        {children}

        {editable && (
          <>
            <span className="dwh-tools">
              <span className="dwh-tool dwh-tool--grip" data-dw-grip title="끌어서 위치 변경" aria-hidden>
                <GripVertical size={12} />
              </span>
              {/* ★ 아이콘 「위젯 설정」은 뺐다(2026-09-09 사용자 지시) — 같은 것이 ⋯ 메뉴 안에도
                     있어 도구가 둘이었다. 도구 줄은 좁을수록 제목이 살아난다. */}
              <button
                type="button" ref={menu.anchorRef}
                className={`dwh-tool${open ? ' is-open' : ''}`} aria-label="더 보기"
                aria-haspopup="menu" aria-expanded={open} onClick={menu.toggle}
              >
                <MoreHorizontal size={12} aria-hidden />
              </button>
            </span>

            {/* 폭 손잡이 — 편집 중에만 보인다(CSS). 격자 밖으로는 못 늘어난다. */}
            <span
              className="dwx-resize" role="separator" aria-label="폭 조절"
              onPointerDown={onResizeDown}
            />

            {menu.pos && createPortal(
              <>
                <button type="button" className="dw-menu__scrim" aria-label="메뉴 닫기" onClick={menu.close} />
                <div
                  className="dw-menu dw-menu--wide dw-menu--fixed" role="menu" aria-label="위젯 메뉴"
                  style={{ top: menu.pos.top, left: menu.pos.left }}
                >
                  <div className="dw-menu__label"><Columns size={13} aria-hidden />폭</div>
                  {([4, 6, 8, 12] as WidgetSpan[]).map((span) => (
                    <button
                      key={span}
                      type="button"
                      role="menuitem"
                      className={`dw-menu__item${placed.span === span ? ' is-on' : ''}`}
                      onClick={() => { setWidgetSpan(board.id, placed.id, span); menu.close(); }}
                    >
                      {span === 4 ? '1/3' : span === 6 ? '1/2' : span === 8 ? '2/3' : '전체'}
                    </button>
                  ))}
                  <div className="dw-menu__sep" />
                  <button
                    type="button" role="menuitem" className="dw-menu__item"
                    onClick={() => { menu.close(); setSettingsOpen(true); }}
                  >
                    <SlidersHorizontal size={13} aria-hidden />위젯 설정
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="dw-menu__item dw-menu__item--danger"
                    onClick={() => { removeWidget(board.id, placed.id); menu.close(); }}
                  >
                    <Trash2 size={13} aria-hidden />위젯 제거
                  </button>
                </div>
              </>,
              document.body,
            )}
          </>
        )}

        {settingsOpen && (DASHBOARD_V2_ENABLED
          ? <WidgetSettingsV2Modal boardId={board.id} placed={placed} onClose={() => setSettingsOpen(false)} />
          : <WidgetSettingsModal boardId={board.id} placed={placed} onClose={() => setSettingsOpen(false)} />
        )}
      </div>
    </WidgetChromeContext.Provider>
  );
}
